"use client";

import { Building2, Clock, Mail, MessageSquare } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FadeInUp } from "@/components/ui/motion";
import { Textarea } from "@/components/ui/textarea";
import { trackLeadContactSubmit } from "@/lib/analytics";
import { useTRPC } from "@/trpc/client";

const contactInfo = [
  {
    icon: Mail,
    title: "Email us",
    description: "hello@makeplan.dev",
    detail: "We'll respond within 24 hours",
  },
  {
    icon: MessageSquare,
    title: "Live chat",
    description: "Chat with our team",
    detail: "Available 9am-6pm PST",
  },
  {
    icon: Building2,
    title: "Enterprise",
    description: "Custom solutions",
    detail: "Dedicated account manager",
  },
  {
    icon: Clock,
    title: "Response time",
    description: "< 24 hours",
    detail: "For all inquiries",
  },
];

const ContactPage = () => {
  const trpc = useTRPC();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const submitContactForm = useMutation(
    trpc.contact.submitForm.mutationOptions({
      onSuccess: (data: any) => {
        toast.success(data.message);
        trackLeadContactSubmit("landing_contact", {
          subject: formData.subject || "",
        });
        setFormData({ name: "", email: "", subject: "", message: "" });
      },
      onError: (error: any) => {
        toast.error(error.message || "Failed to send message. Please try again.");
      },
    }),
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Please fill in all required fields.");
      return;
    }

    setIsSubmitting(true);

    try {
      await submitContactForm.mutateAsync({
        name: formData.name,
        email: formData.email,
        subject: formData.subject,
        message: formData.message,
      });
    } catch (error) {
      console.error("Contact form submission error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <main className="pt-16">
        {/* Hero */}
        <section className="py-16 lg:py-24 relative overflow-hidden">
          {/* Background decoration */}
          <div className="absolute top-10 right-1/4 w-[600px] h-[600px] bg-primary/12 rounded-full blur-[140px] pointer-events-none" />
          <div className="absolute bottom-0 left-1/4 w-[450px] h-[450px] bg-accent/15 rounded-full blur-[120px] pointer-events-none" />

          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
            <FadeInUp className="max-w-3xl mx-auto text-center">
              <h1 className="text-4xl sm:text-5xl font-bold text-foreground mb-4">
                Get in <span className="gradient-text">touch</span>
              </h1>
              <p className="text-lg text-muted-foreground">
                Have questions? We'd love to hear from you. Send us a message
                and we'll respond as soon as possible.
              </p>
            </FadeInUp>
          </div>
        </section>

        {/* Contact Info Cards */}
        <section className="pb-12">
          <div className="container pt-10 mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
              {contactInfo.map((item, index) => (
                <FadeInUp key={item.title} delay={index * 0.1}>
                  <div className="p-6 rounded-xl bg-card border border-border hover:border-primary/30 transition-colors">
                    <item.icon className="w-6 h-6 text-primary mb-3" />
                    <h3 className="font-semibold text-foreground mb-1">
                      {item.title}
                    </h3>
                    <p className="text-sm text-foreground/80">
                      {item.description}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {item.detail}
                    </p>
                  </div>
                </FadeInUp>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="py-16 section-feature relative">
          <div className="absolute inset-0 pattern-grid opacity-15 pointer-events-none" />
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div className="max-w-2xl mx-auto">
              <FadeInUp>
                <div className="bg-card border border-border rounded-2xl p-8 shadow-lg">
                  <h2 className="text-2xl font-bold text-foreground mb-6 text-center">
                    Send us a message
                  </h2>

                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">Name *</Label>
                        <Input
                          id="name"
                          placeholder="Your name"
                          value={formData.name}
                          onChange={(e) =>
                            setFormData({ ...formData, name: e.target.value })
                          }
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Email *</Label>
                        <Input
                          id="email"
                          type="email"
                          placeholder="you@company.com"
                          value={formData.email}
                          onChange={(e) =>
                            setFormData({ ...formData, email: e.target.value })
                          }
                          required
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="subject">Subject</Label>
                      <Input
                        id="subject"
                        placeholder="How can we help?"
                        value={formData.subject}
                        onChange={(e) =>
                          setFormData({ ...formData, subject: e.target.value })
                        }
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message">Message *</Label>
                      <Textarea
                        id="message"
                        placeholder="Tell us more about your needs..."
                        rows={5}
                        value={formData.message}
                        onChange={(e) =>
                          setFormData({ ...formData, message: e.target.value })
                        }
                        required
                      />
                    </div>

                    <Button
                      type="submit"
                      size="lg"
                      className="w-full gradient-bg text-primary-foreground"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Sending..." : "Send message"}
                    </Button>

                    <p className="text-xs text-muted-foreground text-center">
                      By submitting this form, you agree to our privacy policy.
                    </p>
                  </form>
                </div>
              </FadeInUp>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default ContactPage;
